<template>
  <div>
    <h1 v-if="isLoading">Loading App...</h1>
    <h1 v-else>{{ appFullName }} - {{ activeApp.svId }}</h1>
    <div v-if="activeApp.isLoaded">
      <b-form inline>
        <label class="mr-sm-2" for="app-status">Application Status</label>
        <b-form-select id="app-status" v-model="statusId" :options="statusOptions" size="sm" />
        <b-button variant="primary" @click="updateProcessingStatus" class="inline-btn" size="sm">Update</b-button>
      </b-form>
      <b-badge v-if="activeApp.completed" variant="success">Completed</b-badge>
      <b-badge v-else variant="warning">In Progress</b-badge>
      <b-badge v-if="activeApp.teamLeader" variant="primary">Team Leader Applicant</b-badge>
      <b-badge v-else variant="secondary">Not a Team Leader Applicant</b-badge>
      <b-badge v-if="activeApp.financialAid" variant="primary">Financial Aid Applicant</b-badge>
      <b-badge v-else variant="secondary">Not a Financial Aid Applicant</b-badge>
      <b-tabs>
        <b-tab title="General">
          <h3>Academic Information</h3>
          <div class="row">
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>School</h5>
                  <span class="list-value">{{ activeApp.data.schoolName }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <h5>School City</h5>
                  <span class="list-value">{{ activeApp.data.schoolCity }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <h5>School State</h5>
                  <span class="list-value">{{ activeApp.data.schoolState}}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>School Country</h5>
                  <span class="list-value">{{ activeApp.data.schoolCountry }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <h5>Proximity to Conference</h5>
                  <span class="list-value" v-if="activeApp.data.closeProximity">Within 50 miles</span>
                  <span class="list-value" v-else>Further than 50 miles</span>
                </b-list-group-item>
                <b-list-group-item>
                  <h5>Expected Graduation Date</h5>
                  <span class="list-value">{{ appExpectedGraduation }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
          <h3>Proof of Enrollment</h3>
          <div v-if="appProofOfEnrollmentIsLoaded" class="row">
            <div class="col">
              <iframe class="proof-of-enrollment" v-if="appProofOfEnrollmentIsPDF" :src="activeApp.proofOfEnrollmentUrl"/>
              <img class="proof-of-enrollment" v-if="appProofOfEnrollmentIsImage" :src="activeApp.proofOfEnrollmentUrl"/>
            </div>
          </div>
          <h3>Areas of Interest</h3>
          <div class="row">
            <div class="col-md-4">
              <b-list-group>
                <b-list-group-item>
                  <i v-if="activeApp.data.interestArt" class="icon ion-checkmark"/>
                  <i v-else class="icon ion-close"/>
                  <span>Art / Animation </span>
                </b-list-group-item>
                <b-list-group-item>
                  <i v-if="activeApp.data.interestProductionDesign" class="icon ion-checkmark"/>
                  <i v-else class="icon ion-close"/>
                  <span>Production / Design </span>
                </b-list-group-item>
                <b-list-group-item>
                  <i v-if="activeApp.data.interestTechnical" class="icon ion-checkmark"/>
                  <i v-else class="icon ion-close"/>
                  <span>Technical Development </span>
                </b-list-group-item>
                <b-list-group-item>
                  <i v-if="activeApp.data.interestWeb" class="icon ion-checkmark"/>
                  <i v-else class="icon ion-close"/>
                  <span>Web Technology </span>
                </b-list-group-item>
                <b-list-group-item>
                  <i v-if="activeApp.data.interestGraphicDesign" class="icon ion-checkmark"/>
                  <i v-else class="icon ion-close"/>
                  <span>Graphic Design </span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div class="col-md-8">
              <b-card header="Other Interests">
                <p class="card-text">{{ activeApp.data.otherInterests }}</p>
              </b-card>
            </div>
          </div>
          <h3>SIGGRAPH Experience</h3>
          <div class="row">
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>SIGGRAPH North America</h5>
                  <span class="list-value">
                    <i v-if="activeApp.data.haveAttended" class="icon ion-checkmark"/>
                    <i v-else class="icon ion-close"/>
                    Attended
                  </span>
                  <span class="list-value">
                    <i v-if="activeApp.data.haveVolunteered" class="icon ion-checkmark"/>
                    <i v-else class="icon ion-close"/>
                    Volunteered
                  </span>
                </b-list-group-item>
                <b-list-group-item v-if="activeApp.data.haveVolunteered">
                  <h5>Years Volunteered</h5>
                  <span class="list-value">{{ appYearsVolunteered }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>SIGGRAPH Asia</h5>
                  <span class="list-value">
                    <i v-if="activeApp.data.haveAttendedAsia" class="icon ion-checkmark"/>
                    <i v-else class="icon ion-close"/>
                    Attended
                  </span>
                  <span class="list-value">
                    <i v-if="activeApp.data.haveVolunteeredAsia" class="icon ion-checkmark"/>
                    <i v-else class="icon ion-close"/>
                    Volunteered
                  </span>
                </b-list-group-item>
                <b-list-group-item v-if="activeApp.data.haveVolunteeredAsia">
                  <h5>Years Volunteered</h5>
                  <span class="list-value">{{ appYearsVolunteeredAsia }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
          <h3>Reference</h3>
          <div class="row">
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>Reference Name</h5>
                  <span class="list-value">{{ activeApp.data.referenceFirstName }} {{ activeApp.data.referenceLastName }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>Reference Type</h5>
                  <span class="list-value">{{ activeApp.data.referenceType }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>Reference Email</h5>
                  <span class="list-value">{{ activeApp.data.referenceEmail }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
        </b-tab>
        <b-tab title="Essays">
          <h3>Student Volunteer Essays</h3>
          <div class="row">
            <div class="col">
              <b-card header="Essay 1 - Interest">
                <div class="row">
                  <div class="col-md-9">
                    <p class="card-text">{{ activeApp.data.essay1 }}</p>
                  </div>
                </div>
              </b-card>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <b-card header="Essay 2 - Interaction">
                <div class="row">
                  <div class="col-md-9">
                    <p class="card-text">{{ activeApp.data.essay2 }}</p>
                  </div>
                </div>
              </b-card>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <b-card header="Essay 3 - Initiative">
                <div class="row">
                  <div class="col-md-9">
                    <p class="card-text">{{ activeApp.data.essay3 }}</p>
                  </div>
                </div>
              </b-card>
            </div>
          </div>
        </b-tab>
        <b-tab v-if="activeApp.financialAid" title="Financial Aid">
          <h3>Financial Assistance</h3>
          <div class="row">
            <div class="col">
              <b-card header="Financial Aid Summary">
                <p class="card-text">{{ activeApp.data.financialAidSummary }}</p>
              </b-card>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>Primary Transport</h5>
                  <span class="list-value">
                    {{ activeApp.data.primaryTransport }}
                  </span><br/>
                  <span v-if="appFinancialAidAndFlying" class="list-value">
                    Airport Code: {{ activeApp.data.airportCode }}
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div class="col">
              <b-list-group>
                <b-list-group-item>
                  <h5>Estimated Travel Cost</h5>
                  <span class="list-value">
                    {{ activeApp.data.estimatedTravelCost }}<br/>
                    <i v-if="activeApp.data.fundedTravel" class="icon ion-checkmark"/>
                    <i v-else class="icon ion-close"/>
                    Partially or Fully Funded
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
        </b-tab>
        <b-tab v-if="activeApp.teamLeader" title="Team Leader">
          <h3>Team Leader Application</h3>
          <div class="row">
            <div class="col">
              <b-card header="Essay 1 - Qualification">
                <p class="card-text">{{ activeApp.data.teamLeaderEssay1 }}</p>
              </b-card>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <b-card header="Essay 2 - Motivation">
                <p class="card-text">{{ activeApp.data.teamLeaderEssay2 }}</p>
              </b-card>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <b-card header="Essay 3 - Awareness">
                <p class="card-text">{{ activeApp.data.teamLeaderEssay3 }}</p>
              </b-card>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import { statuses } from '@/util/SelectLists'
  export default {
    name: 'ViewApp',
    props: ['username'],
    data () {
      return {
        isLoading: true,
        statusOptions: statuses
      }
    },
    computed: {
      statusId: {
        get () {
          return this.$store.state.admin.activeApp.statusId
        },
        set (value) {
          this.$store.commit('admin/updateProcessingStatus', value)
        }
      },
      ...mapState('admin', ['activeApp']),
      ...mapGetters('admin', [
        'appFullName',
        'appExpectedGraduation',
        'appYearsVolunteered',
        'appYearsVolunteeredAsia',
        'appFinancialAidAndFlying',
        'appProofOfEnrollmentIsLoaded',
        'appProofOfEnrollmentIsPDF',
        'appProofOfEnrollmentIsImage'
      ])
    },
    methods: {
      updateProcessingStatus (e) {
        this.$store.dispatch('admin/updateProcessingStatus')
      }
    },
    created: function () {
      if (!this.$store.state.admin.activeApp.isLoaded) {
        this.$store.dispatch('admin/getApp', this.username).then(() => {
          this.isLoading = false
        }).catch(err => {
          console.log(err)
        })
      } else if (this.username !== this.$store.state.admin.activeApp.username) {
        this.isLoading = true
        this.$store.commit('admin/deactivateApp')
        this.$store.dispatch('admin/getApp', this.username).then(() => {
          this.isLoading = false
        }).catch(err => {
          console.log(err)
        })
      } else {
        this.isLoading = false
      }
    }
  }
</script>

<style lang="scss">
  .inline-btn {
    margin: 10px !important;
  }
  .badge {
    margin: 0px 5px 10px 5px;
  }
  .list-value {
    margin: 0px 0px 0px 10px;
  }
  .card {
    margin: 10px 0px;
    .card-text {
      line-height: 2em;
    }
  }
  .ion-checkmark {
    color: #20d200;
  }
  .ion-close {
    color: #ff0000;
  }
  .proof-of-enrollment {
    width: 100%;
  }
  iframe.proof-of-enrollment {
    height: 400px;
  }
</style>



// WEBPACK FOOTER //
// src/components/admin/ViewApp.vue